import { isImageId } from "./keys.ts";

/**
 * Die Anordnung einer Figur in der Reihe — Reihenfolge, Größe, Höhenversatz
 * (ARCHITEKTUR.md 6.2, 6.5, Schritt 4).
 *
 * ⚠️ Diese Datei wird vom Browser **und** vom Server gelesen, wie `shared/umriss.ts`.
 * Der eine Import geht auf `shared/keys.ts`, und das ist erlaubt: die Datei importiert
 * selbst nichts, zieht also keine Node-Typen in den Client. Die Prüfung der Bild-ID
 * zweimal zu schreiben wäre die schlechtere Wahl — zwei Wahrheiten über dieselbe Form.
 *
 * Hier steht nur, was **beide** Seiten wissen müssen: die Grenzen und die Prüfung. Das
 * Rechnen — Umsetzen, Breiten, linke Kanten — steht im Browser, in `src/lib/anordnung.ts`.
 */

/**
 * Die kleinste und die größte Figur, als Faktor auf die Höhe aus dem Umriss.
 *
 * ⚠️ **Gesetzt, nicht gemessen** (`TODO.md`). Belegt ist nur, wogegen die Grenzen
 * schützen: ohne untere Grenze lässt sich eine Figur auf wenige Pixel schrumpfen und ist
 * nicht mehr auswählbar (die 44-px-Regel aus ARCHITEKTUR.md 2.3), ohne obere Grenze
 * schiebt eine einzige Figur die Reihe auf ein Vielfaches der Bildschirmbreite. Ob
 * 0,5 und 2,0 zu Annas Malpraxis passen, weiß nur sie.
 */
export const MIN_GROESSE = 0.5;
export const MAX_GROESSE = 2;

/** Schrittweite des Reglers. Passt zu `GROESSE_STELLEN`: feiner ließe sich nicht
 *  speichern, gröber fühlte sich der Regler wie eine Rasterung an. */
export const GROESSE_SCHRITT = 0.05;

/**
 * Wie weit eine Figur senkrecht verschoben werden darf, in Prozent ihrer eigenen Höhe.
 *
 * **Positiv hebt an, negativ senkt.** Der gedachte Fall ist das Senken: schneidet das
 * Foto die Beine an den Knien ab, ist die Unterkante des Umrisses das Knie — auf der
 * Bodenlinie sähe die Person aus, als kniete sie. Gesenkt verschwindet die Schnittkante
 * unter der Linie, und was darüber steht, sieht aus wie eine stehende Person
 * (ARCHITEKTUR.md 6.2, „nur wenn Beine oder Füße nicht im Bild sind").
 *
 * ⚠️ **25 % ist gesetzt, nicht gemessen** (`TODO.md`). Ein Viertel der Figurenhöhe ist
 * etwa Kniehöhe; mehr würde eine Person versenken, statt sie zu stellen.
 */
export const MAX_VERSATZ = 25;

/**
 * Nachkommastellen, auf die gerundet wird — und warum es zwei verschiedene sind.
 *
 * Gerundet wird überhaupt, weil das Abnahmekriterium von Schritt 6 verlangt, dass die
 * Anordnung beim Wiederöffnen **derselbe** Stand ist. Und auf **beiden** Seiten, wie
 * beim Umriss: der Browser rundet vor dem Senden, der Server vor dem Speichern. Damit
 * hängt die gespeicherte Form nicht daran, was ein Client geschickt hat.
 *
 * Die Größe braucht zwei Stellen, weil der Regler in Schritten von 0,05 läuft. Der
 * Versatz braucht nur eine: ein Zehntelprozent der Figurenhöhe sind bei 132 px
 * Kachelhöhe rund 0,13 px, also nichts, was ein Auge sieht.
 */
export const GROESSE_STELLEN = 2;
export const VERSATZ_STELLEN = 1;

/** Eine Figur in der Reihe. Die waagerechte Lage folgt aus `reihenfolge`, es gibt
 *  keine `x`-Koordinate (ARCHITEKTUR.md 6.5). */
export interface Anordnung {
  bildId: string;
  /** Platz in der Reihe, von 0 an, lückenlos. */
  reihenfolge: number;
  /** Faktor auf die Höhe aus dem Umriss, Standard 1. */
  groesse: number;
  /** Prozent der Figurenhöhe, Standard 0. Positiv hebt an, negativ senkt. */
  versatz: number;
}

function runde(wert: number, stellen: number, min: number, max: number): number {
  const faktor = 10 ** stellen;
  const eingesperrt = Math.min(max, Math.max(min, wert));
  return Math.round(eingesperrt * faktor) / faktor;
}

/** Eine Größe, eingesperrt und gerundet. Idempotent — zweimal angewendet ändert nichts. */
export function rundeGroesse(wert: number): number {
  if (!Number.isFinite(wert)) return 1;
  return runde(wert, GROESSE_STELLEN, MIN_GROESSE, MAX_GROESSE);
}

/** Ein Höhenversatz, eingesperrt und gerundet. */
export function rundeVersatz(wert: number): number {
  if (!Number.isFinite(wert)) return 0;
  return runde(wert, VERSATZ_STELLEN, -MAX_VERSATZ, MAX_VERSATZ);
}

/** Dieselbe Anordnung, gerundet und eingesperrt. */
export function rundeAnordnung(eintrag: Anordnung): Anordnung {
  return {
    bildId: eintrag.bildId,
    reihenfolge: eintrag.reihenfolge,
    groesse: rundeGroesse(eintrag.groesse),
    versatz: rundeVersatz(eintrag.versatz),
  };
}

function istEintrag(value: unknown): value is Anordnung {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const { bildId, reihenfolge, groesse, versatz } = value as Record<string, unknown>;
  if (!isImageId(bildId)) return false;
  if (typeof reihenfolge !== "number" || !Number.isInteger(reihenfolge) || reihenfolge < 0) {
    return false;
  }
  if (typeof groesse !== "number" || !Number.isFinite(groesse)) return false;
  if (typeof versatz !== "number" || !Number.isFinite(versatz)) return false;
  // Der Wertebereich wird **nicht** hier abgewiesen, sondern von `rundeGroesse` und
  // `rundeVersatz` eingesperrt. Ein Regler, der um ein Hundertstel über die Grenze
  // rutscht, soll die ganze Reihe nicht ungespeichert lassen — der Umriss wird aus
  // demselben Grund großzügig behandelt (`shared/umriss.ts`).
  return true;
}

/**
 * Prüft eine ganze Reihe, wie sie über die Leitung kam.
 *
 * Streng ist die Prüfung bei genau einer Sache, und die trägt: **die Reihenfolge muss
 * eine Vertauschung von 0 … n−1 sein.** Zwei Figuren auf Platz 3 wären eine Reihe, deren
 * Abfolge von der Sortierung des SQLite-Index abhängt — also jeden zweiten Aufruf
 * anders. Ein Unique-Index über (`project_id`, `sort_order`) könnte das nicht
 * übernehmen: beim Umsetzen von Platz 55 auf 3 kollidiert jeder Zwischenstand, und
 * SQLite kann eine Prüfung nicht ans Ende der Transaktion verschieben.
 */
export function istGueltigeReihe(value: unknown): value is Anordnung[] {
  if (!Array.isArray(value)) return false;
  if (!value.every(istEintrag)) return false;

  const bildIds = new Set(value.map((eintrag) => eintrag.bildId));
  if (bildIds.size !== value.length) return false;

  const plaetze = new Set(value.map((eintrag) => eintrag.reihenfolge));
  if (plaetze.size !== value.length) return false;
  return value.every((eintrag) => eintrag.reihenfolge < value.length);
}
